import React, { useCallback, useEffect, useMemo, useState } from 'react';
import clsx from 'clsx';
import { Link, useParams } from 'react-router-dom';
import AppSidebar from './AppSidebar';
import { Navbar } from './common/Catalyst/navbar';
import { SidebarLayout } from './common/Catalyst/sidebar-layout';
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogDescription,
  DialogTitle,
} from './common/Catalyst/dialog';
import { Button } from './common/Catalyst/button';
import LoadingSpinner from './LoadingSpinner';
import { amenityService, homeService, reservationOperationsService } from './clients/ApiClients';
import { AmenityReturn, AmenitySuspensionReturn, HomeReturn } from './clients/rust-api-client';
import { canManageReservations } from './constants/UserTypes';
import { getUserTypeFromToken } from './utils/TokenUtils';
import { homeAddressLine } from './home/homeMeta';
import { formatDayLabel, formatMoment, reservationErrorMessage, todayIn } from './reservation/reservationMeta';
import {
  SUSPENSION_STATE_LABELS,
  SuspensionState,
  suspensionProblem,
  suspensionScope,
  suspensionState,
} from './reservation/phaseThreeMeta';
import { helpClass, inputClass, labelClass } from './reservation/formStyles';
import { ErrorAlert } from './common/Custom/ErrorAlert';
import { FieldPicker } from './common/Custom/FieldPicker';

/**
 * Amenity suspensions (#642, phase 3): homes barred from booking, one
 * amenity or all, for a span of days — automatically after repeated
 * no-shows when the policy turns that on, or by a manager with a reason.
 * Lifting one needs a reason too; nothing is ever deleted, so the record
 * shows the same rule applied to every household.
 *
 * Board members read; managers suspend and lift.
 */
const STATE_STYLE: Record<SuspensionState, string> = {
  active: 'bg-warning/15 text-fg',
  scheduled: 'bg-fg/5 text-fg-secondary',
  ended: 'bg-fg/5 text-fg-muted',
  lifted: 'bg-fg/5 text-fg-muted',
};

const ReservationSuspensionsPage: React.FC = () => {
  const { hoaId } = useParams<{ hoaId: string }>();
  const canManage = canManageReservations(getUserTypeFromToken());
  const [suspensions, setSuspensions] = useState<AmenitySuspensionReturn[]>([]);
  const [activeOnly, setActiveOnly] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [lifting, setLifting] = useState<AmenitySuspensionReturn | null>(null);
  const [amenities, setAmenities] = useState<AmenityReturn[]>([]);

  const load = useCallback(async () => {
    if (!hoaId) return;
    setError(null);
    try {
      const res = await reservationOperationsService.listAmenitySuspensions({
        hoaId,
        active: activeOnly ? true : undefined,
      });
      setSuspensions(res.data.suspensions);
    } catch (err) {
      setError(reservationErrorMessage(err, "Couldn't load the suspensions."));
    } finally {
      setLoading(false);
    }
  }, [hoaId, activeOnly]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!hoaId) return;
    amenityService
      .listAmenities({ hoaId })
      .then((r) => setAmenities(r.data.amenities))
      .catch(() => undefined);
  }, [hoaId]);

  const zone = amenities[0]?.timezone ?? null;
  const today = todayIn(zone);
  const thisYear = new Date().getFullYear();
  const sorted = useMemo(
    () => [...suspensions].sort((a, b) => b.starts_on.localeCompare(a.starts_on) || a.home_address.localeCompare(b.home_address)),
    [suspensions]
  );

  return (
    <SidebarLayout sidebar={<AppSidebar currentPage="reservations" />} navbar={<Navbar>{/* Your navbar content */}</Navbar>}>
      <div className="-mx-2 mb-6 flex items-center gap-2 border-b border-line/60 px-2 pb-3 text-[13px]">
        <Link to={`/hoa/${hoaId}/reservations`} className="shrink-0 text-fg-muted hover:text-fg">
          Reservations
        </Link>
        <span className="text-fg-faint" aria-hidden>
          /
        </span>
        <span className="min-w-0 truncate font-medium text-fg">Suspensions</span>
      </div>

      <div className="space-y-4">
        <div className="flex flex-wrap items-center gap-3">
          <h1 className="mr-auto text-2xl/8 font-semibold tracking-tight text-fg">Booking suspensions</h1>
          <label className="flex items-center gap-2 text-[13px] text-fg-secondary">
            <input
              type="checkbox"
              checked={activeOnly}
              onChange={(e) => setActiveOnly(e.target.checked)}
              className="h-3.5 w-3.5 rounded border-line-strong bg-transparent text-accent focus:ring-accent"
            />
            In force only
          </label>
          {canManage && (
            <Button color="sky" onClick={() => setCreating(true)}>
              Suspend a home
            </Button>
          )}
        </div>
        <p className={helpClass}>
          A suspended home cannot book (a manager may still override with a reason). Bookings it already has are
          not cancelled. Automatic suspensions follow the no-show rule in the reservation policy, applied the same
          way to every household.
        </p>

        <ErrorAlert>{error}</ErrorAlert>
        {loading ? (
          <LoadingSpinner />
        ) : sorted.length === 0 ? (
          <p className="text-[13px] text-fg-muted">{activeOnly ? 'No home is suspended.' : 'No suspensions.'}</p>
        ) : (
          <div className="overflow-x-auto rounded-lg border border-line">
            <table className="w-full text-[13px]">
              <thead className="border-b border-line text-left text-[12px] text-fg-muted">
                <tr>
                  <th className="px-3 py-2 font-medium">Home</th>
                  <th className="px-3 py-2 font-medium">Applies to</th>
                  <th className="px-3 py-2 font-medium">Days</th>
                  <th className="px-3 py-2 font-medium">Why</th>
                  <th className="px-3 py-2 font-medium">Status</th>
                  <th className="px-3 py-2" />
                </tr>
              </thead>
              <tbody className="divide-y divide-line/50">
                {sorted.map((s) => {
                  const state = suspensionState(s, today);
                  return (
                    <tr key={s.id} className="align-top">
                      <td className="px-3 py-2">
                        <Link to={`/hoa/${s.hoa_id}/home/${s.home_id}`} className="text-fg hover:underline">
                          {s.home_address}
                        </Link>
                      </td>
                      <td className="px-3 py-2 text-fg-secondary">{suspensionScope(s)}</td>
                      <td className="whitespace-nowrap px-3 py-2 text-fg-secondary">
                        {formatDayLabel(s.starts_on, thisYear)} – {formatDayLabel(s.ends_on, thisYear)}
                      </td>
                      <td className="px-3 py-2 text-fg-secondary">
                        {s.source === 'automatic' && <span className="text-fg-muted">Automatic · </span>}
                        {s.reason}
                        {s.lifted_at && (
                          <span className="block text-[12px] text-fg-muted">
                            Lifted {formatMoment(s.lifted_at)}
                            {s.lift_reason && ` — “${s.lift_reason}”`}
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        <span className={clsx('rounded-full px-2 py-0.5 text-[12px]', STATE_STYLE[state])}>
                          {SUSPENSION_STATE_LABELS[state]}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-right">
                        {canManage && (state === 'active' || state === 'scheduled') && (
                          <Button plain onClick={() => setLifting(s)}>
                            Lift
                          </Button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {canManage && hoaId && (
        <>
          <SuspendDialog
            open={creating}
            hoaId={hoaId}
            today={today}
            amenities={amenities}
            onClose={() => setCreating(false)}
            onCreated={() => {
              setCreating(false);
              load();
            }}
          />
          <LiftDialog
            suspension={lifting}
            onClose={() => setLifting(null)}
            onLifted={() => {
              setLifting(null);
              load();
            }}
          />
        </>
      )}
    </SidebarLayout>
  );
};

/** The "Every amenity" choice; the suspension stores it as no amenity. */
const EVERY_AMENITY = '__every_amenity__';

const SuspendDialog: React.FC<{
  open: boolean;
  hoaId: string;
  today: string;
  amenities: AmenityReturn[];
  onClose: () => void;
  onCreated: () => void;
}> = ({ open, hoaId, today, amenities, onClose, onCreated }) => {
  const [homes, setHomes] = useState<HomeReturn[]>([]);
  const [homeId, setHomeId] = useState('');
  const [amenityId, setAmenityId] = useState('');
  const [startsOn, setStartsOn] = useState(today);
  const [endsOn, setEndsOn] = useState('');
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setHomeId('');
    setAmenityId('');
    setStartsOn(today);
    setEndsOn('');
    setReason('');
    setError(null);
    homeService
      .getHomesByHoa({ hoaId })
      .then((r) => setHomes([...r.data].sort((a, b) => homeAddressLine(a).localeCompare(homeAddressLine(b)))))
      .catch((err) => setError(reservationErrorMessage(err, "Couldn't load the homes.")));
  }, [open, hoaId, today]);

  const problem = suspensionProblem({ homeId, startsOn, endsOn, reason });

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (problem) return;
    setSaving(true);
    setError(null);
    try {
      await reservationOperationsService.createAmenitySuspension({
        amenitySuspensionCreate: {
          hoa_id: hoaId,
          home_id: homeId,
          amenity_id: amenityId || null,
          starts_on: startsOn || null,
          ends_on: endsOn,
          reason: reason.trim(),
        },
      });
      onCreated();
    } catch (err) {
      setError(reservationErrorMessage(err, "Couldn't suspend the home."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} size="lg">
      <form onSubmit={submit}>
        <DialogTitle>Suspend a home from booking</DialogTitle>
        <DialogDescription>
          The household is told, with your reason. Their existing bookings stand.
        </DialogDescription>
        <DialogBody className="space-y-4">
          <div className={labelClass}>
            Home
            <FieldPicker
              size="sm"
              label="Home"
              placeholder="Choose a home…"
              value={homeId}
              onChange={setHomeId}
              options={homes.map((h) => ({ value: h.id, label: homeAddressLine(h) }))}
            />
          </div>
          <div className={labelClass}>
            From
            <FieldPicker
              size="sm"
              label="From"
              // "Every amenity" is a real choice, stored as ''; FieldPicker
              // reads '' as nothing chosen, so it gets a sentinel.
              value={amenityId || EVERY_AMENITY}
              onChange={(v) => setAmenityId(v === EVERY_AMENITY ? '' : v)}
              options={[
                { value: EVERY_AMENITY, label: 'Every amenity' },
                ...amenities
                  .filter((a) => a.booking_mode !== 'info_only')
                  .map((a) => ({ value: a.id, label: a.name })),
              ]}
            />
          </div>
          <div className="flex flex-wrap gap-3">
            <label className={labelClass}>
              First day
              <input type="date" value={startsOn} onChange={(e) => setStartsOn(e.target.value)} className={inputClass} />
            </label>
            <label className={labelClass}>
              Last day
              <input type="date" value={endsOn} onChange={(e) => setEndsOn(e.target.value)} className={inputClass} />
            </label>
          </div>
          <label className={labelClass}>
            Reason — the household is shown this
            <textarea
              rows={3}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. Clubhouse left damaged after the 12 September booking."
              className={clsx(inputClass, 'resize-y')}
            />
          </label>
          {problem && (homeId || endsOn || reason) && <ErrorAlert>{problem}</ErrorAlert>}
          <ErrorAlert>{error}</ErrorAlert>
        </DialogBody>
        <DialogActions>
          <Button plain onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button color="sky" type="submit" disabled={!!problem || saving}>
            {saving ? 'Suspending…' : 'Suspend'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

const LiftDialog: React.FC<{
  suspension: AmenitySuspensionReturn | null;
  onClose: () => void;
  onLifted: () => void;
}> = ({ suspension, onClose, onLifted }) => {
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setReason('');
    setError(null);
  }, [suspension]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!suspension || !reason.trim()) return;
    setSaving(true);
    setError(null);
    try {
      await reservationOperationsService.liftAmenitySuspension({
        suspensionId: suspension.id,
        amenitySuspensionLift: { reason: reason.trim() },
      });
      onLifted();
    } catch (err) {
      setError(reservationErrorMessage(err, "Couldn't lift the suspension."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={suspension !== null} onClose={onClose} size="md">
      <form onSubmit={submit}>
        <DialogTitle>Lift the suspension</DialogTitle>
        <DialogDescription>
          {suspension?.home_address} · {suspension ? suspensionScope(suspension) : ''}
        </DialogDescription>
        <DialogBody className="space-y-3">
          <label className={labelClass}>
            Reason
            <textarea
              rows={3}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className={clsx(inputClass, 'resize-y')}
            />
          </label>
          <ErrorAlert>{error}</ErrorAlert>
        </DialogBody>
        <DialogActions>
          <Button plain onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button color="sky" type="submit" disabled={!reason.trim() || saving}>
            {saving ? 'Lifting…' : 'Lift suspension'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default ReservationSuspensionsPage;
